<script lang="ts" setup>
import { useQuasar } from 'quasar';
import { useRoute, useRouter } from 'vue-router';
import {
  useDashboardStore,
  useMessagesStore,
  useRealtimeStore,
  useSessionStore
} from 'src/store';
import {
  computed,
  onActivated,
  onMounted,
  onUnmounted,
  watch,
} from 'vue';
import { loadPatientInfoAndTasks } from 'src/pages/TheDataHydrator';
import { handleApiRequest } from 'src/api/handleApiRequest';
import { getMessages } from 'src/api/endpoints/messagesWeb';
import { getEocSummaries } from 'src/api/endpoints/statisticsWeb';
import Box from 'src/components/BoxContainer.vue';
import Navigation from 'src/components/DashboardNavigation.vue';
import DashboardNotification from 'src/components/DashboardNotifications.vue';
import {useAiSummariesStore} from "@/store/aiSummariesStore.ts";

const $q = useQuasar(),
  route = useRoute(),
  router = useRouter();

const dashboardStore = useDashboardStore(),
  messagesStore = useMessagesStore(),
  realtimeStore = useRealtimeStore(),
  sessionStore = useSessionStore(),
  aiSummariesStore = useAiSummariesStore();

const hideLeftColumn = computed(() => sessionStore.isMagicLinkUser);

const alerts = computed(() => dashboardStore.alerts?.filter(x => x.visible) || []),
  isLoading = computed(() => dashboardStore.isLoading),
  unreadMsgCt = computed(() => messagesStore.unreadMessagesCount);

let unsubscribeRealtime: (() => void) | null = null;
let unsubscribeNewMessage: (() => void) | null = null;
let unsubscribeNewEpisodeOfCareSummary: (() => void) | null = null;

onActivated(async () => {
  //Force redirect to change password
  if (sessionStore.requiresPasswordChange)
    router.push({ name: 'Settings.ChangePassword', params: { forcePasswordChange: 'true' } });

  await loadPatientInfoAndTasks(sessionStore, messagesStore, dashboardStore, aiSummariesStore, $q);

  dashboardStore.setTodaysDate();
})

// Lift the SignalR connection to the dashboard shell so unread badges update
// in real time anywhere in the dashboard (not just on the Messages page).
// `realtime.start()` is idempotent — safe even if Vue chooses to remount.
//
// Handler registration happens here unconditionally (cheap, no network).
// They sit dormant until realtimeStore.start() succeeds. If the server says
// the feature is disabled (see watch below), start() never runs and these
// handlers never fire.
onMounted(() => {
  // Badge handler: update unread count directly from the server-pushed value.
  // No API round-trip needed for non-null payloads.
  // Synthetic reconnect replay (payload === null) refetches to close any gap.
  unsubscribeRealtime = realtimeStore.onUnreadCount(async (payload) => {
    const currentEocId = sessionStore.user?.selectedEocId;
    if (payload && currentEocId !== undefined && payload.eocId !== currentEocId) return;

    if (payload === null) {
      const resp = await handleApiRequest(() => getMessages(), null, $q);
      if (resp) messagesStore.loadMessages(resp.result);
    } else {
      messagesStore.setUnreadMessagesCount(payload.unreadCount);
    }
  });

  // Global newMessage handler. Keeps the messages store fresh anywhere in
  // the dashboard (not just on the Messages page). Without this, events
  // that arrive while the patient is on Home/another tab are dropped because
  // no page-scoped handler is registered. Both null payload (reconnect
  // replay) and non-null require a refetch — the payload only carries
  // eocId+timestamp+senderConnectionId, not the message body.
  unsubscribeNewMessage = realtimeStore.onNewMessage(async (payload) => {
    const currentEocId = sessionStore.user?.selectedEocId;
    if (payload && currentEocId !== undefined && payload.eocId !== currentEocId) return;

    // Skip events the current connection initiated — the sender's tab
    // already refreshed via attemptSend's local reload. Other tabs and
    // browsers of the same user fall through and process normally because
    // their connection ID doesn't match.
    if (payload && payload.senderConnectionId
        && payload.senderConnectionId === realtimeStore.connectionId) {
      return;
    }

    const resp = await handleApiRequest(() => getMessages(), null, $q);
    if (resp) messagesStore.loadMessages(resp.result);
  });

  // MM-41660: Global newEpisodeOfCareSummary handler. Keeps the AI summaries store fresh anywhere in the
  // dashboard (Home cards and the full Recovery Updates list both read from aiSummariesStore). The payload only
  // carries episodeOfCareId+createdDate, not the summary body, so a refetch is required. (CC)
  unsubscribeNewEpisodeOfCareSummary = realtimeStore.onNewEpisodeOfCareSummary(async (payload) => {
    const currentEocId = sessionStore.user?.selectedEocId;
    if (!dashboardStore.patientConfig?.features?.isAIPatientSummariesEnabled) return;
    if (payload && currentEocId !== undefined && payload.episodeOfCareId !== currentEocId) return;
    if (!currentEocId) return;

    const eocSumsResp = await handleApiRequest(() => getEocSummaries(currentEocId), null, $q);
    if (eocSumsResp) aiSummariesStore.setAiSummariesStore(eocSumsResp.result);
  });
});

// Wait for the patient config to load, then start realtime IF the server says
// the feature is enabled. The watch fires once the flag is a defined boolean
// (avoiding the race where onMounted runs before patientConfig is hydrated by
// onActivated → loadPatientInfoAndTasks). The boolean guard ensures we only
// act on the FIRST definitive value — subsequent fires (e.g., EOC switches
// during the same session) are ignored. The watch itself stays alive until
// the component unmounts (via Vue's automatic setup-watcher cleanup, plus
// the explicit stopFlagWatch() below as belt-and-suspenders).
//
// NOTE on why we don't call stopFlagWatch() from inside the callback:
// `immediate: true` runs the callback SYNCHRONOUSLY during the watch() call,
// before `const stopFlagWatch = watch(...)` finishes binding. Referencing
// stopFlagWatch from inside the callback then would hit the TDZ
// ("Cannot access 'stopFlagWatch' before initialization"). The boolean
// guard avoids the self-reference entirely.
//
// MM-41660: the connection is shared by every SignalR-backed feature, so it starts if EITHER messaging real-time
// or AI patient summaries is enabled — a patient with only one of the two features still needs the connection
// for that one. (CC)
let realtimeStartHandled = false;
const stopFlagWatch = watch(
  () => {
    const features = dashboardStore.patientConfig?.features;
    return features ? (features.isMessagingRealTimeEnabled || features.isAIPatientSummariesEnabled) : undefined;
  },
  async (enabled) => {
    if (realtimeStartHandled) return;     // already acted on the first definitive value
    if (enabled === undefined) return;    // patient config not yet loaded — keep watching

    realtimeStartHandled = true;          // mark before any await so concurrent fires no-op

    if (!enabled) return;                 // server says realtime is off in this environment

    try {
      await realtimeStore.start();
    } catch {
      // Non-fatal: badge stays at last-fetched value;
      // auto-reconnect handles transient drops; user reload recovers
      // from cold-start failure.
    }
  },
  { immediate: true }   // fire immediately if the config was already loaded
);

onUnmounted(() => {
  // Belt-and-suspenders — Vue auto-cleans setup-level watches on unmount,
  // but being explicit doesn't hurt and documents intent.
  stopFlagWatch();
  unsubscribeRealtime?.();
  unsubscribeRealtime = null;
  unsubscribeNewMessage?.();
  unsubscribeNewMessage = null;
  unsubscribeNewEpisodeOfCareSummary?.();
  unsubscribeNewEpisodeOfCareSummary = null;
});
</script>

<template>
  <q-page class="dashboard" padding>
    <div class="row bs-gutter justify-center">

      <!-- Box -->
      <Box :box-padding="$zb.enums.BoxPaddingEnum.p303055" :box-width="$zb.enums.BoxWidthEnum.xl"
        class="col col-md-auto">
        <div class="row">

          <!-- Left Column -->
          <div class="col-auto gt-sm dashboard__left" v-if="!hideLeftColumn">

            <!-- Navigation -->
            <div class="row dashboard__navigation" >
              <div class="col">
                <Navigation :unread-msg-ct="unreadMsgCt" />
              </div>
            </div>

            <!-- Auxiliary -->
            <div class="row pt-30">
              <div class="col">
                <router-view v-slot="{ Component }" name="auxiliary">
                  <keep-alive>
                    <component :is="Component" />
                  </keep-alive>
                </router-view>
              </div>
            </div>
          </div>

          <!-- Right Column -->
          <div class="col dashboard__right">

            <!-- Notifications -->
            <div v-if="alerts" class="row dashboard__notif">
              <div class="col">
                <zbm-skeleton-loader class="mb-30" height="48px" :loading="isLoading" width="100%">
                  <DashboardNotification v-for="(alert, i) in alerts" @dismiss-notif="(value) => value.visible = false"
                    :alert="alert" :key="`banner-${i}`" />
                </zbm-skeleton-loader>
              </div>
            </div>

            <!-- Default -->
            <div class="row">
              <div class="col">
                <router-view v-slot="{ Component }">
                  <keep-alive>
                    <component :is="Component" />
                  </keep-alive>
                </router-view>
              </div>
            </div>
          </div>
        </div>
      </Box>
    </div>
  </q-page>
</template>

<style lang="scss" scoped>
.dashboard {
  $dash-brdr: 1px solid $border-clr;

  // Left Column (only visible at md, 1024px, breakpoint and wider)
  &__left {
    // [class*="col"]
    $dash-left-col-w: 248px + 30px + 1px; // content space + right padding + border width

    width: $dash-left-col-w !important;
    max-width: $dash-left-col-w !important;
    flex-basis: $dash-left-col-w;
    padding-inline-end: 30px;
    border-inline-end: $dash-brdr;
  }

  // Navigation
  &__navigation {
    // .row
    padding-block-end: 22px;
    border-block-end: $dash-brdr;
  }

  // Right Column
  &__right {
    // [class*="col"]
    overflow-x: auto;
  }

  // Notifications
  &__notif>[class*="col"] {
    // .row

    :deep(> div:empty:not(.q-skeleton)) {
      margin-block-end: 0 !important;
    }
  }

  @media (min-width: $breakpoint-md-min) {

    // Right Column
    &__right {
      // [class*="col"]
      padding-inline-start: 30px;
    }
  }
}
</style>